// @core/stores/task/TaskStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { taskApi } from '@core/api';
import type {
  AddChecklistRequest,
  AddDependencyRequest,
  AddTaskWatcherRequest,
  AssignEntityRequest,
  AssignTaskRequest,
  ChangeEntityTaskStatusRequest,
  ChangeTaskStatusRequest,
  CreateTaskFromTemplateRequest,
  CreateTaskRequest,
  GetAssigneeAvailabilityRequest,
  GetAssigneeAvailabilityResponse,
  GetAvailableTransitionsResponse,
  GetDependencyGraphResponse,
  GetTaskCustomFieldsResponse,
  ListActivityResponse,
  ListDependenciesResponse,
  ListTimeEntriesResponse,
  LogEntityTimeRequest,
  LogTimeRequest,
  RescheduleTaskRequest,
  ScheduleTaskRequest,
  SetTaskCustomFieldRequest,
  SuggestTaskScheduleRequest,
  SuggestTaskScheduleResponse,
  Task,
  TaskLifecycleResponse,
  TaskResponse,
  TimeEntryResponse,
  ToggleChecklistItemRequest,
  UpdateChecklistItemRequest,
  UpdateTaskRequest,
} from '@core/types/task';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class TaskStore {
  // List state
  items: Task[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Task | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Task>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Task | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Task | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  /**
   * Drop the cached copy so the next GetTask actually hits the network.
   * List pages populate the cache with rows that lack the detail-only parts
   * (checklists, watchers, aspects), so a detail view that trusts the cache
   * renders a task without them. NOTE: hand-added — corebuilder regen of
   * this store drops it again (framework-kpsu); reapply after regen.
   */
  invalidateTask(id: string): void {
    this.removeFromCache(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add checklist
   */
  async AddChecklist(task_id: string, data: AddChecklistRequest): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.AddChecklist(task_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Add dependency
   */
  async AddDependency(task_id: string, data: AddDependencyRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.AddDependency(task_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Add task watcher
   */
  async AddTaskWatcher(task_id: string, data: AddTaskWatcherRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.AddTaskWatcher(task_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Assign entity
   */
  async AssignEntity(target_type: string, target_id: string, data: AssignEntityRequest): Promise<TaskLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.AssignEntity(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Assign task
   */
  async AssignTask(task_id: string, data: AssignTaskRequest): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.AssignTask(task_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Change entity task status
   */
  async ChangeEntityTaskStatus(target_type: string, target_id: string, data: ChangeEntityTaskStatusRequest): Promise<TaskLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.ChangeEntityTaskStatus(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Change task status
   */
  async ChangeTaskStatus(task_id: string, data: ChangeTaskStatusRequest): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.ChangeTaskStatus(task_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create task
   */
  async CreateTask(data: CreateTaskRequest): Promise<Task> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.CreateTask(data);
      const item = response.task;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create task from template
   */
  async CreateTaskFromTemplate(template_id: string, data: CreateTaskFromTemplateRequest): Promise<Task> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.CreateTaskFromTemplate(template_id, data);
      const item = response.task;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete task
   */
  async DeleteTask(task_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await taskApi.DeleteTask(task_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== task_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === task_id) {
          this.currentItem = null;
        }

        this.removeFromCache(task_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete time entry
   */
  async DeleteTimeEntry(task_id: string, entry_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await taskApi.DeleteTimeEntry(task_id, entry_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== entry_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === entry_id) {
          this.currentItem = null;
        }

        this.removeFromCache(entry_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get assignee availability
   */
  async GetAssigneeAvailability(data: GetAssigneeAvailabilityRequest): Promise<GetAssigneeAvailabilityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.GetAssigneeAvailability(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get available transitions
   */
  async GetAvailableTransitions(task_id: string): Promise<GetAvailableTransitionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.GetAvailableTransitions(task_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get dependency graph
   */
  async GetDependencyGraph(params?: { }): Promise<GetDependencyGraphResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.GetDependencyGraph(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get task
   */
  async GetTask(task_id: string): Promise<Task> {
    // Check cache first
    const cached = this.cache.get(task_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await taskApi.GetTask(task_id);
      const item = response.task;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get task custom fields
   */
  async GetTaskCustomFields(task_id: string): Promise<GetTaskCustomFieldsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.GetTaskCustomFields(task_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List task activity
   */
  async ListActivity(task_id: string, params?: PaginatedRequest): Promise<ListActivityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.ListActivity(task_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List dependencies
   */
  async ListDependencies(task_id: string, params?: PaginatedRequest): Promise<ListDependenciesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.ListDependencies(task_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List entity activity
   */
  async ListEntityActivity(target_type: string, target_id: string, params?: PaginatedRequest): Promise<ListActivityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.ListEntityActivity(target_type, target_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List my tasks
   */
  async ListMyTasks(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await taskApi.ListMyTasks(params);

      runInAction(() => {
        this.items = response.tasks?.tasks ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List ready tasks
   */
  async ListReadyTasks(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await taskApi.ListReadyTasks(params);

      runInAction(() => {
        this.items = response.tasks?.tasks ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List scheduled tasks
   */
  async ListScheduledTasks(params?: PaginatedRequest & { time_max?: string | number | boolean; time_min?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await taskApi.ListScheduledTasks(params);

      runInAction(() => {
        this.items = response.tasks?.tasks ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List tasks
   */
  async ListTasks(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await taskApi.ListTasks(params);

      runInAction(() => {
        this.items = response.tasks?.tasks ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List tasks by parent
   */
  async ListTasksByParent(entity_type: string, entity_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await taskApi.ListTasksByParent(entity_type, entity_id, params);

      runInAction(() => {
        this.items = response.tasks?.tasks ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List time entries
   */
  async ListTimeEntries(task_id: string, params?: PaginatedRequest): Promise<ListTimeEntriesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.ListTimeEntries(task_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Log entity time
   */
  async LogEntityTime(target_type: string, target_id: string, data: LogEntityTimeRequest): Promise<TimeEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.LogEntityTime(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Log time
   */
  async LogTime(task_id: string, data: LogTimeRequest): Promise<TimeEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.LogTime(task_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove dependency
   */
  async RemoveDependency(task_id: string, dependency_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.RemoveDependency(task_id, dependency_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove custom field value
   */
  async RemoveTaskCustomField(task_id: string, field_id: string): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.RemoveTaskCustomField(task_id, field_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove task watcher
   */
  async RemoveTaskWatcher(task_id: string, watcher_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.RemoveTaskWatcher(task_id, watcher_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reschedule task
   */
  async RescheduleTask(task_id: string, data: RescheduleTaskRequest): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.RescheduleTask(task_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Schedule task
   */
  async ScheduleTask(task_id: string, data: ScheduleTaskRequest): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.ScheduleTask(task_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set custom field value
   */
  async SetTaskCustomField(task_id: string, field_id: string, data: SetTaskCustomFieldRequest): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.SetTaskCustomField(task_id, field_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Suggest task schedule
   */
  async SuggestTaskSchedule(task_id: string, data: SuggestTaskScheduleRequest): Promise<SuggestTaskScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.SuggestTaskSchedule(task_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Toggle checklist item
   */
  async ToggleChecklistItem(task_id: string, checklist_id: string, item_id: string, data: ToggleChecklistItemRequest): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.ToggleChecklistItem(task_id, checklist_id, item_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Unassign task
   */
  async UnassignTask(task_id: string): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.UnassignTask(task_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Unschedule task
   */
  async UnscheduleTask(task_id: string): Promise<TaskResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await taskApi.UnscheduleTask(task_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update checklist item
   */
  async UpdateChecklistItem(task_id: string, checklist_id: string, item_id: string, data: UpdateChecklistItemRequest): Promise<Task> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.UpdateChecklistItem(task_id, checklist_id, item_id, data);
      const item = response.task;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update task
   */
  async UpdateTask(task_id: string, data: UpdateTaskRequest): Promise<Task> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.UpdateTask(task_id, data);
      const item = response.task;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const taskStore = new TaskStore();
